import { Entity, ThemeSchemaResponse } from '@api';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useGraphRelations } from '@terminal/react-query-hooks/graph/useGraphRelations';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { SourceEvidenceSection } from '@terminal/widgets/theme-widget/tabs/theme-exposure-tab/components/source-evidence-section/SourceEvidenceSection';
import { useTranslation } from 'react-i18next';
import { StepperSection } from '../components/stepper-section/StepperSection';
import styles from './CompanyExposureTab.module.scss';

export const CompanyFinancialMacroExposureTab = ({
  entity,
  theme,
}: {
  entity: Entity;
  theme: ThemeSchemaResponse;
}) => {
  const { t } = useTranslation(['widget']);

  const { data, isLoading, error } = useGraphRelations({
    nodeType: 'theme',
    node: theme.id,
    relationKind: 'theme-company',
    direction: 'outbound',
    maxRank: 75,
  });

  const themeCompany = data?.edges.find(
    edge => edge.target === `company/${entity.tag}`
  );

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '180px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (error || !themeCompany) {
    throw new Error(
      error?.message ||
        'CompanyFinancialMacroExposureTab: no theme company found'
    );
  }

  const { description, evidence, source_evidence, rank } =
    themeCompany.attributes;

  return (
    <div className={styles.root}>
      <StepperSection
        entity={entity}
        theme={theme}
        title={t('widget:themeOverview.drawer.entityExposureToTheme', {
          entity: entity.name_short || entity.name,
          theme: theme.name,
        })}
        activeIndex={0}
        description={description}
      />
      <div className={styles.sectionRow}>
        <div className={styles.titleBase}>
          {t('widget:themeOverview.drawer.companyExposureRank')}
        </div>
        <div className={styles.styledRankWrapper}>
          <div className={styles.rankContainer}>
            <div className={styles.rankLabel}>
              {t(
                'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
              )}
            </div>
            <div className={styles.rankFlexRow}>
              <TopRankIcon rank={rank} />
            </div>
          </div>
        </div>
      </div>
      <ErrorBoundaryWrapper>
        <SourceEvidenceSection
          evidence={evidence}
          source={POSTHOG_TRACKING_SOURCES.COMPANY_TO_THEME_DRAWER}
          sourceEvidence={source_evidence}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
